import React from "react";
import { connect } from "../../stores";
import { BannerType, getOneBanner, PageType, TargetParams } from "../../utils/BannerContainer";
import Customer from "../../datatypes/customer/Customer";
import { PositionRules } from "../../services/PositionRulesService";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
import { getShopFlavor } from "../../utils/ShopFlavor";
import FeatureUtil from "../../utils/FeatureUtil";
import { withReactAdapter } from "../../module-federation/withReactAdapter";

type Props = {
    customer: Customer | null | undefined;
    customerIdFromSessionStore: string | null | undefined;
    positionRules: PositionRules | null | undefined;
    page: PageType;
    type: BannerType;
    targetParams: Object | null | undefined;
    loadingCssClass: string | null | undefined;
    onBannerLoaded: () => void;
};

type GlobalParams = {
    customerId?: string | null | undefined;
    customerType: string;
    segment: string | null | undefined;
    locale: string;
    customerStore?: string | undefined | null;
    customerTargetGroup?: string | undefined | null;
    customerCategoryGroup?: string[] | undefined | null;
    flavor?: string | undefined | null;
};

export class Banner extends React.Component<Props> {
    getGlobalParams(): GlobalParams {
        const customer = this.props.customer;
        const customerId = this.props.customerIdFromSessionStore;
        const customerTargetGroup = customer?.targetGroup;
        const customerCategoryGroup = customer?.categoryGroups;
        const customerSegment = customer?.segment;
        const customerStore = SessionStore.getCustomerStore();
        const locale = SessionStore.getLocale();
        const shopFlavor = getShopFlavor();
        let customerType = "anon";
        if (customer) {
            customerType = customer.validForFsd ? "fsd" : "nonFsd";
        }
        const result: GlobalParams = {
            customerType: customerType,
            segment: customerSegment,
            locale: locale
        };

        if (customerStore) {
            result.customerStore = customerStore;
        }

        if (customerId) {
            result.customerId = customerId;
        }

        if (customerTargetGroup) {
            result.customerTargetGroup = customerTargetGroup;
        }
        if (FeatureUtil.isCCGPlacementActive() && customerCategoryGroup) {
            result.customerCategoryGroup = customerCategoryGroup;
        }
        if (shopFlavor) {
            result.flavor = shopFlavor;
        }
        return result;
    }

    showBanner() {
        // If the current page and type is valid, i.e. contained in the positionRules, show the banner
        if (
            this.props.positionRules &&
            this.props.positionRules.pageToType[this.props.page] &&
            this.props.positionRules.pageToType[this.props.page].includes(this.props.type)
        ) {
            return true;
        }
        // else dont show banner
        return false;
    }

    renderContent(
        page: PageType,
        type: BannerType,
        targetParams: Object | null | undefined,
        onBannerLoaded?: () => void
    ) {
        const customerId = this.props.customerIdFromSessionStore;
        const globalParams: GlobalParams = this.getGlobalParams();
        const allTargetParams = { ...{}, ...targetParams, ...globalParams };
        if (!customerId) {
            // not logged in and that's ok (anonymous):
            return (
                <div className="test-ex-banner-anonymous">
                    {getOneBanner(page, type, allTargetParams as TargetParams, onBannerLoaded)}
                </div>
            );
        }
        if (this.props.customer) {
            // logged in and customer loaded:
            return (
                <div className="test-ex-banner-loaded">
                    {getOneBanner(page, type, allTargetParams as TargetParams, onBannerLoaded)}
                </div>
            );
        }
        // logged in but not loaded yet:
        if (this.props.loadingCssClass) {
            const classes = "test-ex-banner-loading " + this.props.loadingCssClass;
            return <div className={classes} />;
        }
        return null;
    }

    renderTileContent(
        page: PageType,
        type: BannerType,
        targetParams: Object | null | undefined,
        onBannerLoaded?: () => void
    ) {
        const customerId = this.props.customerIdFromSessionStore;
        const globalParams: GlobalParams = this.getGlobalParams();
        const allTargetParams = { ...{}, ...targetParams, ...globalParams };
        const banner = getOneBanner(page, type, allTargetParams as TargetParams, onBannerLoaded);
        if (banner) {
            if (!customerId || this.props.customer) {
                return banner;
            }
        }

        // logged in but not loaded yet:
        if (this.props.loadingCssClass) {
            const classes = "test-ex-banner-loading " + this.props.loadingCssClass;
            return <div className={classes} />;
        }
        return null;
    }

    render() {
        if (!this.showBanner()) {
            return null;
        }
        if (this.props.type === "tileBanner") {
            return this.renderTileContent(
                this.props.page,
                this.props.type,
                this.props.targetParams,
                this.props.onBannerLoaded
            );
        }

        const content = this.renderContent(
            this.props.page,
            this.props.type,
            this.props.targetParams,
            this.props.onBannerLoaded
        );

        return <div className="ex-banner">{content}</div>;
    }
}

export default withReactAdapter(
    connect(
        {
            stores: {
                CustomerStore: (s: any) => ({
                    customer: s.getCustomer()
                }),
                SessionStore: (s: any) => ({
                    customerIdFromSessionStore: s.getCustomerId()
                }),
                // FIXME this is a hack! The loading of position rules is racy, therefore the banner may try to render without the rules being loaded yet
                // which results in the banner never being shown.
                PositionRulesStore: (s: any) => ({
                    positionRules: s.getState()
                })
            }
        },
        Banner
    )
);
